import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineLockClosed, HiOutlineUser } from "react-icons/hi2";
import { IoAirplaneOutline } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { adminEnvironment } from "../config/environment";
import useAuth from "../hooks/useAuth";
import { getDefaultPathForRole } from "../app/navigation";
import { can, CAPABILITY } from "../auth/permissions";
import { Alert } from "../ui/Alert";
import Button from "../ui/Button";
import { Checkbox, Field, Input } from "../ui/Field";
import { describeRequestError } from "../ui/States";

// The inputs are forced LTR (`ltr-value`), so their own inline start is the
// physical left, while the icon sits at the wrapper's start (the right in
// Arabic). Reserve the icon space on the icon's side in both directions so
// right-aligned Arabic-mode text never runs under it.
const ICON_INPUT_PADDING = "ps-10 rtl:ps-3.5 rtl:pe-10";

function Login() {
  const { t } = useTranslation();
  const { login, persist, setPersist, sessionEndReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [credentials, setCredentials] = useState({ userIdentity: "", password: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    setCredentials((previous) => ({ ...previous, [event.target.name]: event.target.value }));
    setError(null);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const session = await login(credentials);
      const from = location.state?.from?.pathname;
      const hasPanelAccess = can(session.role, CAPABILITY.PANEL_ACCESS);
      const target = !hasPanelAccess
        ? "/unauthorized"
        : from && from !== "/login" && from !== "/unauthorized"
          ? from
          : getDefaultPathForRole(session.role);
      navigate(target, { replace: true });
    } catch (failure) {
      const status = Number(failure?.response?.status || 0);
      // The server answers ACCOUNT_SUSPENDED only after the password was
      // verified, so saying so discloses nothing to a wrong-password guess.
      if (failure?.response?.data?.code === "ACCOUNT_SUSPENDED") {
        setError({ suspended: true });
      } else {
        setError(status === 401 || status === 404 || status === 400 ? { invalid: true } : { failure });
      }
      setLoading(false);
    }
  };

  const errorMessage = error
    ? error.suspended
      ? t("auth.accountSuspended")
      : error.invalid
        ? t("auth.invalidCredentials")
        : describeRequestError(error.failure, t).message
    : null;
  const isProduction = adminEnvironment.environment === "production";

  return (
    <main className="min-h-screen bg-surface-muted">
      <div className="mx-auto grid min-h-screen max-w-6xl lg:grid-cols-[1fr_minmax(380px,460px)]">
        <section className="relative hidden flex-col justify-between overflow-hidden bg-slate-950 p-10 text-white lg:flex xl:p-14">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-accent-300 to-brand-600">
              <IoAirplaneOutline size={22} aria-hidden="true" />
            </span>
            <div>
              <p className="text-base font-semibold tracking-tight">{t("app.name")}</p>
              <p className="text-[11px] text-slate-400">{t("app.controlCenter")}</p>
            </div>
          </div>
          <div className="max-w-md">
            <h1 className="text-3xl font-semibold leading-tight tracking-tight">{t("auth.heroTitle")}</h1>
            <p className="mt-4 text-sm leading-7 text-slate-300">{t("auth.heroText")}</p>
          </div>
          <p className="text-xs text-slate-500">{t("auth.secureSession")}</p>
        </section>

        <section className="flex flex-col bg-white p-6 sm:p-10 lg:border-s lg:border-slate-200">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 lg:hidden">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950 text-accent-300">
                <IoAirplaneOutline size={20} aria-hidden="true" />
              </span>
              <span className="font-semibold text-slate-950">{t("app.name")}</span>
            </div>
            <div className="ms-auto">
              <LanguageSwitcher compact />
            </div>
          </div>

          <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
            <p
              className={`inline-flex w-fit items-center gap-2 rounded-md border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${
                isProduction ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
              data-testid="login-environment"
            >
              {adminEnvironment.environment} · <span className="ltr-value normal-case tracking-normal">{adminEnvironment.apiHost}</span>
            </p>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">{t("auth.signIn")}</h2>
            <p className="mt-1 text-sm text-slate-500">{t("auth.signInSubtitle")}</p>

            {sessionEndReason === "expired" && !error ? (
              <Alert tone="warning" className="mt-5">
                {t("shell.sessionExpired")}
              </Alert>
            ) : null}
            {sessionEndReason === "suspended" && !error ? (
              <Alert tone="danger" className="mt-5" role="alert" title={t("auth.accountSuspendedTitle")}>
                {t("shell.accountSuspended")}
              </Alert>
            ) : null}

            <form className="mt-6 space-y-4" onSubmit={handleLogin} noValidate data-allow-autofill>
              <Field label={t("auth.username")} required>
                {(props) => (
                  <span className="relative block">
                    <HiOutlineUser className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" size={17} aria-hidden="true" />
                    <Input
                      {...props}
                      id="userIdentity"
                      type="text"
                      name="userIdentity"
                      value={credentials.userIdentity}
                      onChange={handleChange}
                      placeholder={t("auth.username")}
                      autoComplete="username"
                      autoCapitalize="off"
                      spellCheck={false}
                      required
                      invalid={Boolean(error)}
                      className={ICON_INPUT_PADDING}
                      ltr
                    />
                  </span>
                )}
              </Field>

              <Field label={t("auth.password")} required>
                {(props) => (
                  <span className="relative block">
                    <HiOutlineLockClosed className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" size={17} aria-hidden="true" />
                    <Input
                      {...props}
                      id="password"
                      type="password"
                      name="password"
                      value={credentials.password}
                      onChange={handleChange}
                      placeholder={t("auth.password")}
                      autoComplete="current-password"
                      required
                      invalid={Boolean(error)}
                      className={ICON_INPUT_PADDING}
                      ltr
                    />
                  </span>
                )}
              </Field>

              {errorMessage ? (
                <Alert tone="danger" role="alert">
                  {errorMessage}
                </Alert>
              ) : null}

              <Checkbox
                id="persist"
                label={t("auth.rememberMe")}
                checked={persist}
                onChange={() => setPersist((previous) => !previous)}
              />

              <Button type="submit" size="lg" className="w-full" loading={loading}>
                {loading ? t("auth.signingIn") : t("auth.signIn")}
              </Button>
            </form>

            <p className="mt-6 text-center text-xs leading-5 text-slate-400">{t("auth.accountSupport")}</p>
          </div>
        </section>
      </div>
    </main>
  );
}

export default Login;
